import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/AxiosConfig";
import "../../Styles/userorder.css";
import {
  DEFAULT_PRODUCT_IMAGE,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import { showError, showSuccess } from "../../utils/Toast";
import { getSafePaymentErrorMessage } from "../../utils/paymentErrors";
import ProductReviewModal from "../Review/ProductReviewModal";
import DownloadInvoiceButton from "../Common/DownloadInvoiceButton";
import { getOrderPricingSummary } from "../../utils/orderSummary";
import { resolveReviewDisplayState } from "../../utils/reviewEligibility";
import { getViewportIsMobile } from "../../utils/browser";
import { clearBuyerSession } from "../../utils/authStorage";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiHeadphones,
  FiHeart,
  FiLogOut,
  FiMapPin,
  FiPackage,
  FiPrinter,
  FiUser,
} from "react-icons/fi";

const STATUS_LABELS = {
  pending: "Pending",
  pending_payment: "Pending Payment",
  placed: "Order Placed",
  order_placed: "Order Placed",
  confirmed: "Confirmed",
  accepted: "Accepted",
  accept: "Accepted",
  processing: "Processing",
  packed: "Packed",
  ready_to_ship: "Ready to Ship",
  shipment_created: "Shipment Created",
  picked_up: "Picked Up",
  dispatched: "Shipped",
  shipped: "Shipped",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  paid: "Paid",
  completed: "Paid",
  success: "Paid",
  cancelled: "Cancelled",
  rejected: "Rejected",
  returned: "Returned",
  refunded: "Refunded",
  failed: "Failed",
  failed_delivery: "Failed Delivery",
  unavailable: "Unavailable",
};

const STATUS_DESCRIPTIONS = {
  placed: "Your order has been received.",
  order_placed: "Your order has been received.",
  confirmed: "We have confirmed your order.",
  accepted: "Your order has been accepted.",
  processing: "Your items are being prepared for shipment.",
  packed: "Your package has been packed.",
  ready_to_ship: "Your package is ready to ship.",
  shipment_created: "Shipment has been created with the courier.",
  picked_up: "Your package has been picked up by the courier.",
  dispatched: "Your order is on the way.",
  shipped: "Your order is on the way.",
  in_transit: "Your shipment is moving through the network.",
  out_for_delivery: "Your package is out for delivery.",
  delivered: "Your order has been delivered.",
  cancelled: "This order has been cancelled.",
  rejected: "This order has been rejected.",
  returned: "This order has been returned.",
  refunded: "This order has been refunded.",
  failed: "Delivery could not be completed.",
  failed_delivery: "Delivery could not be completed.",
  unavailable: "This order is currently unavailable.",
};

const PROGRESS_STEPS = [
  { key: "placed", label: "Order Placed" },
  { key: "accepted", label: "Accepted" },
  { key: "processing", label: "Processing" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

const TERMINAL_STATUSES = new Set([
  "cancelled",
  "rejected",
  "returned",
  "refunded",
  "failed",
  "failed_delivery",
  "unavailable",
]);

const normalizeStatusKey = (status) => {
  const normalized = String(status || "pending")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (normalized === "order_placed") return "placed";
  if (normalized === "accept") return "accepted";
  return normalized;
};

const getEffectivePaymentStatus = (order = {}, paymentStatus, paymentMethod) => {
  const normalizedPaymentStatus = normalizeStatusKey(paymentStatus);

  if (
    normalizedPaymentStatus === "paid" ||
    normalizedPaymentStatus === "completed" ||
    normalizedPaymentStatus === "success"
  ) {
    return "paid";
  }

  return normalizedPaymentStatus || "pending";
};

const getReadableStatus = (status) => {
  const normalized = normalizeStatusKey(status);
  return (
    STATUS_LABELS[normalized] ||
    normalized
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") ||
    "Pending"
  );
};

const getStatusDescription = (status) => {
  const normalized = normalizeStatusKey(status);
  return STATUS_DESCRIPTIONS[normalized] || "Status update received.";
};

const getStatusClassName = (status) =>
  normalizeStatusKey(status).replace(/_/g, "-");

const getProgressStageKey = (status) => {
  const normalized = normalizeStatusKey(status);

  if (["placed", "pending", "pending_payment"].includes(normalized)) {
    return "placed";
  }
  if (["confirmed", "accepted"].includes(normalized)) {
    return "accepted";
  }
  if (normalized === "processing") {
    return "processing";
  }
  if (
    [
      "packed",
      "ready_to_ship",
      "shipment_created",
      "picked_up",
      "dispatched",
      "shipped",
      "in_transit",
      "out_for_delivery",
    ].includes(normalized)
  ) {
    return "shipped";
  }
  if (normalized === "delivered") {
    return "delivered";
  }

  return "placed";
};

const formatDateTime = (value, options = {}) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: options.includeYear === false ? undefined : "numeric",
    ...(options.includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
};

const formatCurrency = (value) => `AED ${Number(value || 0).toFixed(2)}`;

const getFirstPresentValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (
      !normalized ||
      normalized.toLowerCase() === "null" ||
      normalized.toLowerCase() === "undefined"
    ) {
      continue;
    }
    return value;
  }
  return null;
};

const formatDisplayValue = (value, fallback = "Not available") => {
  const resolved = getFirstPresentValue(value);
  if (!resolved) return fallback;
  return String(resolved).trim();
};

const formatDisplayDate = (value, fallback = "Not available") => {
  const formatted = formatDateTime(value);
  if (formatted) return formatted;
  return formatDisplayValue(value, fallback);
};

const getPaymentMethodLabel = (paymentMethod) => {
  const normalized = String(paymentMethod || "")
    .trim()
    .toLowerCase();

  if (!normalized) return "Not specified";
  if (normalized === "cod") return "Cash on Delivery";
  if (normalized === "stripe") return "Stripe Card Payment";
  if (normalized === "card") return "Card Payment";
  if (normalized === "online") return "Online Payment";
  if (normalized === "bank_transfer") return "Bank Transfer";

  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const parseAttributes = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getVariantSummary = (item) => {
  const attributes =
    parseAttributes(item?.product_snapshot?.attributes) ||
    parseAttributes(item?.attributes) ||
    null;

  if (!attributes || typeof attributes !== "object") return "";

  const keys = ["variant", "size", "color", "unit", "storage"];
  const values = keys
    .map((key) => {
      const raw = attributes[key];
      return raw ? `${key.charAt(0).toUpperCase() + key.slice(1)}: ${raw}` : "";
    })
    .filter(Boolean);

  return values.join(" | ");
};

const getItemImage = (item) =>
  withDefaultProductImage(
    item?.product_snapshot?.image_url ||
      item?.product_image ||
      item?.product?.image_url ||
      item?.product?.image_urls?.[0],
  );

const getItemName = (item) =>
  item?.product_snapshot?.name ||
  item?.product_name ||
  item?.product?.name ||
  "Product";

const buildFallbackProgressSteps = (status, createdAt) => {
  const currentStageKey = getProgressStageKey(status);
  const currentStageIndex = Math.max(
    PROGRESS_STEPS.findIndex((step) => step.key === currentStageKey),
    0,
  );

  return PROGRESS_STEPS.map((step, index) => ({
    key: step.key,
    label: step.label,
    timestamp: index === 0 ? createdAt : null,
    completed: TERMINAL_STATUSES.has(normalizeStatusKey(status))
      ? index <= currentStageIndex
      : index < currentStageIndex || (normalizeStatusKey(status) === "delivered" && index === currentStageIndex),
    active:
      !TERMINAL_STATUSES.has(normalizeStatusKey(status)) &&
      index === currentStageIndex,
  }));
};

const buildFallbackStatusTimeline = (status, createdAt, updatedAt) => {
  const normalized = normalizeStatusKey(status);

  return [
    {
      key: "placed",
      label: "Order Placed",
      timestamp: createdAt,
      completed: true,
      active: normalized === "placed",
    },
    ...(normalized !== "placed"
      ? [
          {
            key: normalized,
            label: getReadableStatus(normalized),
            timestamp: updatedAt || null,
            completed: true,
            active: true,
          },
        ]
      : []),
  ];
};

const buildProgressFromTimeline = (timeline = [], status, fallbackCreatedAt = null) =>
  buildFallbackProgressSteps(status, fallbackCreatedAt).map((step) => {
    const source = timeline.find((entry) => normalizeStatusKey(entry.key) === step.key)
      || timeline.find((entry) =>
        !TERMINAL_STATUSES.has(normalizeStatusKey(entry.key)) &&
        getProgressStageKey(entry.key) === step.key,
      );
    return { ...step, timestamp: source?.timestamp || step.timestamp };
  });

const normalizeProgressSteps = (steps = []) =>
  steps.map((step) => ({
    ...step,
    label: normalizeStatusKey(step?.key) === "delivered" ? "Delivered" : step?.label,
  }));

function Userorder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId: routeOrderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [retryingPayment, setRetryingPayment] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviewItem, setSelectedReviewItem] = useState(null);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 0,
    review_text: "",
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewOverrides, setReviewOverrides] = useState({});

  const stateOrderId = location.state?.orderId;
  const resolvedOrderId = Number(routeOrderId || stateOrderId || 0);

  useEffect(() => {
    setIsMobile(getViewportIsMobile());

    const handleResize = () => {
      setIsMobile(getViewportIsMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadOrderDetail = async (targetOrderId) => {
    if (!Number.isSafeInteger(targetOrderId) || targetOrderId <= 0) {
      setOrder(null);
      setErrorMessage("Invalid order ID.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const [orderResponse, paymentResponse] = await Promise.all([
        axiosInstance.get(`orders/${targetOrderId}`),
        axiosInstance
          .get(`user/orders/${targetOrderId}/payment`)
          .catch(() => null),
      ]);

      if (!orderResponse.data?.success || !orderResponse.data?.data) {
        throw new Error("Order not found.");
      }
      setOrder({
        ...orderResponse.data.data,
        payment_detail: paymentResponse?.data?.success
          ? paymentResponse.data.data
          : null,
      });
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        setErrorMessage("Please sign in to view this order.");
      } else if (status === 403) {
        setErrorMessage("You are not allowed to view this order.");
      } else if (status === 404) {
        setErrorMessage("Order not found.");
      } else {
        setErrorMessage("Unable to load order details right now.");
      }
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrderDetail(resolvedOrderId);
  }, [resolvedOrderId]);

  const handlePrint = () => {
    window.print();
  };

  const handleCancelOrder = async () => {
    if (!resolvedOrderId || !window.confirm("Cancel this order?")) return;

    try {
      setCancelling(true);
      await axiosInstance.put(`orders/${resolvedOrderId}/cancel`, {
        note: "Cancelled from buyer order detail",
      });
      await loadOrderDetail(resolvedOrderId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Unable to cancel the order right now.",
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleBackToOrders = () => {
    navigate("/account", {
      state: {
        navigationname: "orders",
      },
    });
  };

  const handleAccountNavigation = (section) => {
    if (section === "logout") {
      if (window.confirm("Log out of your account?")) {
        clearBuyerSession();
        showSuccess("Logged out successfully");
        navigate("/");
      }
      return;
    }
    navigate("/account", { state: { navigationname: section } });
  };

  const handleContactSupport = () => {
    navigate("/contact");
  };

  const handleRetryPayment = async () => {
    if (!resolvedOrderId || retryingPayment) return;

    try {
      setRetryingPayment(true);
      const retryWithTamara = normalizedPaymentMethod === "tamara";
      const retryWithTabby = normalizedPaymentMethod === "tabby";
      const response = await axiosInstance.post(
        retryWithTamara ? "tamara/create-checkout-session" : retryWithTabby ? "tabby/create-checkout-session" : "stripe/create-checkout-session",
        {
        order_id: resolvedOrderId,
        payment_method: retryWithTamara ? "tamara" : retryWithTabby ? "tabby" : "stripe",
        ...(retryWithTamara || retryWithTabby ? { checkout_request_id: window.crypto.randomUUID() } : {}),
      });

      if (response.data?.status && response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      throw new Error(response.data?.message || "Unable to start payment checkout.");
    } catch (error) {
      setErrorMessage(
        getSafePaymentErrorMessage(
          error,
          "Payment could not be started. Please try again.",
        ),
      );
    } finally {
      setRetryingPayment(false);
    }
  };

  const getReviewOverrideKey = (orderId, itemId, productId) =>
    `${orderId}:${itemId || productId}`;

  const resetReviewModal = () => {
    setShowReviewModal(false);
    setSelectedReviewItem(null);
    setReviewFormData({
      rating: 0,
      review_text: "",
    });
    setIsSubmittingReview(false);
  };

  const openReviewModal = (item) => {
    setSelectedReviewItem({
      order_id: resolvedOrderId,
      order_number: order?.order_number || resolvedOrderId,
      order_item_id: item.item_id || item.id || null,
      product_id: item.product_id,
      product_name: getItemName(item),
      product_image: getItemImage(item),
      review_submitted: Boolean(item.review_submitted),
      review_id: item.review_id || null,
      review_rating: item.review_rating || null,
      review_status: item.review_status || null,
    });
    setReviewFormData({
      rating: Number(item.review_rating || 0),
      review_text: "",
    });
    setShowReviewModal(true);
  };

  const handleReviewTextChange = (event) => {
    const value = event.target.value;
    setReviewFormData((prev) => ({
      ...prev,
      review_text: value,
    }));
  };

  const handleReviewRatingChange = (rating) => {
    setReviewFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  const handleSubmitReview = async () => {
    if (!selectedReviewItem || isSubmittingReview) return;

    if (!reviewFormData.rating || reviewFormData.rating < 1 || reviewFormData.rating > 5) {
      showError("Please select a rating between 1 and 5");
      return;
    }

    const trimmedReview = String(reviewFormData.review_text || "").trim();
    if (trimmedReview.length < 3) {
      showError("Please enter at least 3 characters for your review");
      return;
    }

    if (trimmedReview.length > 1000) {
      showError("Review text must not exceed 1000 characters");
      return;
    }

    try {
      setIsSubmittingReview(true);
      const response = await axiosInstance.post("reviews", {
        order_id: selectedReviewItem.order_id,
        order_item_id: selectedReviewItem.order_item_id,
        product_id: selectedReviewItem.product_id,
        rating: reviewFormData.rating,
        review_text: trimmedReview,
      });

      const reviewData = response?.data?.data || {};
      setReviewOverrides((prev) => ({
        ...prev,
        [getReviewOverrideKey(
          selectedReviewItem.order_id,
          selectedReviewItem.order_item_id,
          selectedReviewItem.product_id,
        )]: {
          can_review: false,
          review_submitted: true,
          review_id: reviewData.review_id || null,
          review_rating: reviewData.rating || reviewFormData.rating,
          review_status: reviewData.status || "pending",
        },
      }));

      showSuccess(response?.data?.message || "Review submitted successfully");
      resetReviewModal();
      await loadOrderDetail(resolvedOrderId);
    } catch (error) {
      showError(error.response?.data?.message || "Failed to submit review");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="userorder-loading">
        <div className="loading-spinner"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="userorder-not-found" role="alert">
        <FiPackage size={64} />
        <h2>{errorMessage === "Invalid order ID." ? "Invalid Order ID" : errorMessage?.includes("sign in") || errorMessage?.includes("not allowed") ? "Order Access Unavailable" : errorMessage?.includes("Unable to load") ? "Unable to Load Order" : "Order Not Found"}</h2>
        <p>{errorMessage || "The order you're looking for doesn't exist."}</p>
        <button className="userorder-primary-btn" onClick={handleBackToOrders}>
          <FiArrowLeft /> Back to My Orders
        </button>
      </div>
    );
  }

  const currentStatusKey = normalizeStatusKey(
    order.display_status ||
      order.current_status ||
      order.latest_status ||
      order.order_status ||
      order.status ||
      "placed",
  );
  const currentStatusLabel = getReadableStatus(currentStatusKey);
  const progressSteps = normalizeProgressSteps(
    Array.isArray(order.delivery_progress_steps) &&
      order.delivery_progress_steps.length
      ? order.delivery_progress_steps
      : Array.isArray(order.status_timeline) && order.status_timeline.length
        ? buildProgressFromTimeline(order.status_timeline, currentStatusKey, order.created_at)
        : buildFallbackProgressSteps(currentStatusKey, order.created_at),
  );
  const timelineEntries =
    Array.isArray(order.status_timeline) && order.status_timeline.length
      ? order.status_timeline
      : buildFallbackStatusTimeline(
          currentStatusKey,
          order.created_at,
          order.updated_at,
        );
  const recentActivity = [...timelineEntries].reverse().slice(0, 3);
  const latestShipment = order.latest_shipment || order.shipments?.[0] || null;
  const trackingSummary = order.tracking_summary || null;
  const trackingNumber = getFirstPresentValue(
    order.tracking_id,
    order.tracking_number,
    order.awb_number,
    latestShipment?.awb_number,
    order.courier_tracking_number,
    trackingSummary?.tracking_number,
  );
  const estimatedDeliveryValue = getFirstPresentValue(
    order.estimated_delivery,
    order.estimatedDelivery,
    order.delivery_estimate,
    order.shipment?.estimated_delivery,
    order.tracking?.estimated_delivery,
    order.shipment?.estimatedDelivery,
    order.tracking?.estimatedDelivery,
    latestShipment?.estimated_delivery,
    latestShipment?.estimatedDelivery,
    latestShipment?.tracking_response?.estimated_delivery,
    latestShipment?.tracking_response?.estimatedDelivery,
    latestShipment?.tracking_response?.expected_delivery,
    latestShipment?.tracking_response?.expectedDelivery,
    latestShipment?.tracking_response?.eta,
    trackingSummary?.estimated_delivery,
    trackingSummary?.estimatedDelivery,
    trackingSummary?.expected_delivery,
    trackingSummary?.expectedDelivery,
    trackingSummary?.eta,
  );
  const estimatedDeliveryLabel = formatDisplayDate(estimatedDeliveryValue);
  const orderItems = Array.isArray(order.items) ? order.items : [];
  const orderItemsWithReviewState = orderItems.map((item) => {
    const itemId = item.item_id || item.id || null;
    const reviewOverride =
      reviewOverrides[
        getReviewOverrideKey(resolvedOrderId, itemId, item.product_id)
      ] || null;
    const normalizedReviewItem = {
      ...item,
      item_id: itemId,
      delivery_status: item.delivery_status || order.delivery_status || null,
      order_status: item.order_status || order.order_status || null,
      status: item.status || order.status || order.display_status || null,
      delivered_at: item.delivered_at || order.delivered_at || null,
      shipment_status:
        item.shipment_status ||
        order.latest_shipment?.shipment_status ||
        order.tracking_summary?.status ||
        null,
    };
    const { canReview, reviewSubmitted } = resolveReviewDisplayState({
      item: normalizedReviewItem,
      order,
      reviewOverride,
    });

    return {
      ...normalizedReviewItem,
      can_review: canReview,
      review_submitted: reviewSubmitted,
      review_id: reviewOverride?.review_id ?? item.review_id ?? null,
      review_rating:
        reviewOverride?.review_rating ?? item.review_rating ?? null,
      review_status:
        reviewOverride?.review_status ?? item.review_status ?? null,
    };
  });
  const paymentDetail = order.payment_detail || null;
  const latestPayment = paymentDetail?.latest_payment || order.latest_payment || null;
  const rawPaymentStatus =
    paymentDetail?.payment_status ||
    order.payment_status ||
    latestPayment?.status ||
    "pending";
  const effectivePaymentMethod =
    paymentDetail?.payment_method ||
    order.payment_method ||
    latestPayment?.payment_method ||
    latestPayment?.provider ||
    null;
  const effectivePaymentStatus = getEffectivePaymentStatus(
    {
      ...order,
      shipment_status:
        order.latest_shipment?.shipment_status ||
        order.tracking_summary?.status ||
        null,
    },
    rawPaymentStatus,
    effectivePaymentMethod,
  );
  const normalizedPaymentMethod = String(effectivePaymentMethod || "")
    .trim()
    .toLowerCase();
  const canRetryOnlinePayment =
    Boolean(resolvedOrderId) &&
    ["pending", "failed", "expired", "cancelled"].includes(
      normalizeStatusKey(rawPaymentStatus),
    ) &&
    ["stripe", "card", "online", "tamara", "tabby"].includes(normalizedPaymentMethod);
  const pricingSummary = getOrderPricingSummary(order);
  const subtotal = Number(pricingSummary.subtotal || 0);
  const vatAmount = Number(pricingSummary.vatAmount || 0);
  const shippingCharge = Number(pricingSummary.deliveryCharge || 0);
  const discountAmount = Number(pricingSummary.discountAmount || 0);
  const grandTotal = Number(pricingSummary.total || 0);
  const progressReachedCount = progressSteps.filter((step) => step.completed || step.active).length;
  const progressPercent = progressSteps.length > 1
    ? ((Math.max(progressReachedCount, 1) - 1) / (progressSteps.length - 1)) * 100
    : 0;
  const canCancelOrder = [
    "pending",
    "pending_payment",
    "placed",
    "confirmed",
  ].includes(currentStatusKey);
  const shippingAddress = order.shipping_address || null;
  const shippingLocality = [
    shippingAddress?.city,
    shippingAddress?.state,
    shippingAddress?.postal_code || shippingAddress?.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="userorder-page">
      <div className="userorder-container">
        <header className="userorder-hero">
          <button type="button" className="userorder-back-btn" onClick={handleBackToOrders}>
            <FiArrowLeft aria-hidden="true" /> Back to My Orders
          </button>
          <div className="userorder-hero-copy">
            <h1>Track Your Order</h1>
            <p>Follow the latest updates for this order.</p>
          </div>
        </header>

        <nav className="userorder-account-nav" aria-label="Account sections">
          {[
            { key: "orders", label: "My Orders", icon: <FiFileText /> },
            { key: "profile", label: "My Profile", icon: <FiUser /> },
            { key: "wishlist", label: "Wishlist", icon: <FiHeart /> },
            { key: "addresses", label: "Saved Addresses", icon: <FiMapPin /> },
            { key: "logout", label: "Logout", icon: <FiLogOut /> },
          ].map((section) => (
            <button
              key={section.key}
              type="button"
              className={section.key === "orders" ? "is-active" : ""}
              aria-current={section.key === "orders" ? "page" : undefined}
              onClick={() => handleAccountNavigation(section.key)}
            >
              <span aria-hidden="true">{section.icon}</span>{section.label}
            </button>
          ))}
        </nav>

        {errorMessage ? <div className="userorder-inline-error" role="alert">{errorMessage}</div> : null}

        <section className="userorder-card userorder-overview" aria-labelledby="userorder-number">
          <div className="userorder-overview-head">
            <div className="summary-copy">
              <div className="summary-title-row">
                <h2 id="userorder-number">Order {order.order_number || `#${order.id || order.order_id}`}</h2>
                <span className={`userorder-status-badge ${getStatusClassName(currentStatusKey)}`}>
                  {currentStatusLabel}
                </span>
              </div>
              <p className="summary-subtitle">
                Placed {formatDateTime(order.created_at, { includeTime: true }) || "date unavailable"}
              </p>
            </div>
            <div className="summary-actions">
              {canCancelOrder ? (
                <button type="button" className="userorder-secondary-btn" onClick={handleCancelOrder} disabled={cancelling}>
                  {cancelling ? "Cancelling..." : "Cancel Order"}
                </button>
              ) : null}
              <button type="button" className="userorder-secondary-btn" onClick={handlePrint}>
                <FiPrinter aria-hidden="true" /> Print
              </button>
              <DownloadInvoiceButton order={order} className="userorder-secondary-btn" />
            </div>
          </div>

          <div className="userorder-delivery-meta">
            <div><span>Tracking ID</span><strong>{formatDisplayValue(trackingNumber, "Not available yet")}</strong></div>
            <div><span>Estimated delivery</span><strong>{estimatedDeliveryValue ? estimatedDeliveryLabel : "Not available yet"}</strong></div>
          </div>

          <div className="userorder-progress" role="group" aria-label="Delivery progress">
            <div className="progress-track">
              <div className="progress-track-bar" aria-hidden="true"></div>
              <div className="progress-track-fill" style={{ width: `${progressPercent}%` }} aria-hidden="true"></div>
              <ol className="progress-steps">
                {progressSteps.map((step) => (
                  <li
                    key={step.key}
                    className={`progress-step ${step.completed ? "completed" : ""} ${step.active ? "active" : ""}`}
                    aria-current={step.active ? "step" : undefined}
                  >
                    <span className="progress-step-icon" aria-hidden="true">
                      {step.completed ? <FiCheckCircle /> : <FiClock />}
                    </span>
                    <span className="progress-step-copy">
                      <span className="progress-step-label">{step.label}</span>
                      <span className="progress-step-state">{step.active ? "Current" : step.completed ? "Completed" : "Upcoming"}</span>
                      {step.timestamp ? <span className="progress-step-date">{formatDateTime(step.timestamp, { includeYear: false })}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {TERMINAL_STATUSES.has(currentStatusKey) ? (
            <p className="progress-terminal-note">Order status: {currentStatusLabel}. Delivery steps stop at the last confirmed update.</p>
          ) : null}

          <div className="userorder-activity">
            <h3>Recent activity</h3>
            <ol>
              {recentActivity.map((entry, index) => (
                <li key={`${entry.key}-${entry.timestamp || index}`}>
                  <span className="activity-icon" aria-hidden="true">{index === 0 ? <FiPackage /> : <FiClock />}</span>
                  <span className="activity-copy">
                    <strong>{entry.label}</strong>
                    <span>{getStatusDescription(entry.key)}</span>
                  </span>
                  {entry.timestamp ? <time dateTime={entry.timestamp}>{formatDateTime(entry.timestamp, { includeTime: true })}</time> : null}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <div className="userorder-content-grid">
          <div className="userorder-main-column">

            <section className="userorder-card">
              <div className="card-head">
                <div>
                  <h3>Order Items</h3>
                </div>
              </div>

              <div className="detail-items-list">
                {orderItemsWithReviewState.map((item) => {
                  const variantSummary = getVariantSummary(item);
                  const unitPrice = Math.max(0, Number(item.unit_price || 0) - Number(item.discount || 0));
                  const lineTotal = Number(item.line_total ?? item.total ?? (Number(item.quantity || 0) * unitPrice));

                  return (
                    <div
                      key={
                        item.id ||
                        item.item_id ||
                        `${item.product_id}-${item.quantity}`
                      }
                      className={`detail-item-row ${isMobile ? "mobile" : ""}`}
                    >
                      <div className="detail-item-media">
                        <img
                          src={getItemImage(item)}
                          alt={getItemName(item)}
                          onError={(event) => {
                            event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                          }}
                        />
                      </div>

                      <div className="detail-item-copy">
                        <h4>{getItemName(item)}</h4>
                        {variantSummary ? (
                          <p className="detail-item-variant">
                            {variantSummary}
                          </p>
                        ) : null}
                        <div className="detail-item-meta">
                          <span>Qty: {item.quantity}</span>
                          {item.seller_name ? (
                            <span>Seller: {item.seller_name}</span>
                          ) : null}
                          {item.item_status ? (
                            <span>
                              Status: {getReadableStatus(item.item_status)}
                            </span>
                          ) : null}
                        </div>
                        <div className="detail-review-actions">
                          {item.can_review ? (
                            <button
                              type="button"
                              className="add-review-btn"
                              onClick={() => openReviewModal(item)}
                            >
                              Add Review
                            </button>
                          ) : item.review_submitted ? (
                            <div className="review-submitted-pill">
                              Review Submitted
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="detail-item-pricing">
                        <strong>{formatCurrency(unitPrice)}</strong>
                        <span>{formatCurrency(lineTotal)} total</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="detail-items-footer">
                <span>
                  {orderItemsWithReviewState.reduce((total, item) => total + Number(item.quantity || 0), 0)} item{orderItemsWithReviewState.reduce((total, item) => total + Number(item.quantity || 0), 0) === 1 ? "" : "s"}
                </span>
                <strong>Total: {formatCurrency(grandTotal)}</strong>
              </div>
            </section>
            <section className="userorder-card">
              <div className="card-head">
                <div>
                  <h3>Payment Summary</h3>
                </div>
              </div>

              <div className="payment-panel">
                <div className="payment-row">
                  <span className="payment-label">
                    <FiCreditCard /> Method
                  </span>
                  <strong>{getPaymentMethodLabel(effectivePaymentMethod)}</strong>
                </div>
                <div className="payment-row">
                  <span className="payment-label">Payment Status</span>
                  <span
                    className={`payment-status-chip ${getStatusClassName(
                      effectivePaymentStatus,
                    )}`}
                  >
                    {getReadableStatus(effectivePaymentStatus)}
                  </span>
                </div>
                {latestPayment?.failure_message ? (
                  <div className="tracking-fallback">
                    {latestPayment.failure_message}
                  </div>
                ) : null}
                {latestPayment?.receipt_url ? (
                  <div className="payment-row">
                    <span className="payment-label">Receipt</span>
                    <a
                      href={latestPayment.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                      className="userorder-secondary-btn"
                      style={{ textDecoration: "none" }}
                    >
                      Open Receipt
                    </a>
                  </div>
                ) : null}
                {latestPayment?.updated_at ? (
                  <div className="payment-row">
                    <span className="payment-label">Last Update</span>
                    <strong>
                      {formatDateTime(latestPayment.updated_at, {
                        includeTime: true,
                      })}
                    </strong>
                  </div>
                ) : null}
                <div className="payment-row">
                  <span className="payment-label">Subtotal</span>
                  <strong>{formatCurrency(subtotal)}</strong>
                </div>
                {vatAmount > 0 ? (
                  <div className="payment-row">
                    <span className="payment-label">VAT</span>
                    <strong>{formatCurrency(vatAmount)}</strong>
                  </div>
                ) : null}
                {shippingCharge > 0 ? (
                  <div className="payment-row">
                    <span className="payment-label">Delivery Charge</span>
                    <strong>{formatCurrency(shippingCharge)}</strong>
                  </div>
                ) : null}
                {discountAmount > 0 ? (
                  <div className="payment-row">
                    <span className="payment-label">Discount</span>
                    <strong>-{formatCurrency(discountAmount)}</strong>
                  </div>
                ) : null}
                <div className="payment-total-row">
                  <span>Total</span>
                  <strong>{formatCurrency(grandTotal)}</strong>
                </div>
                {canRetryOnlinePayment ? (
                  <button
                    className="userorder-primary-btn"
                    onClick={handleRetryPayment}
                    disabled={retryingPayment}
                    style={{ marginTop: "12px", width: "100%" }}
                  >
                    <FiCreditCard />
                    {retryingPayment ? "Redirecting..." : "Retry Secure Payment"}
                  </button>
                ) : null}
              </div>
            </section>
          </div>

          <div className="userorder-side-column">
            <section className="userorder-card">
              <div className="card-head">
                <div>
                  <h3>Shipping Address</h3>
                </div>
              </div>

              {shippingAddress ? (
                <div className="address-panel">
                  {shippingAddress.name ? (
                    <div className="address-line strong">
                      <FiUser /> {shippingAddress.name}
                    </div>
                  ) : null}
                  {shippingAddress.address_line1 ? (
                    <div className="address-line">
                      <FiMapPin /> {shippingAddress.address_line1}
                    </div>
                  ) : null}
                  {shippingAddress.address_line2 ? (
                    <div className="address-line">
                      {shippingAddress.address_line2}
                    </div>
                  ) : null}
                  {shippingLocality ? (
                    <div className="address-line">{shippingLocality}</div>
                  ) : null}
                  {shippingAddress.country ? (
                    <div className="address-line">
                      {shippingAddress.country}
                    </div>
                  ) : null}
                  {shippingAddress.phone ? (
                    <div className="address-line">{shippingAddress.phone}</div>
                  ) : null}
                  {shippingAddress.email ? (
                    <div className="address-line">{shippingAddress.email}</div>
                  ) : null}
                </div>
              ) : (
                <div className="tracking-fallback">
                  Shipping address will appear once the order address is
                  available.
                </div>
              )}
            </section>

            <section className="userorder-card userorder-support-card">
              <FiHeadphones className="support-icon" />
              <h3>Need Help with Your Order?</h3>
              <p>Our support team is here to help with your order questions.</p>
              <div className="support-actions">
                <button
                  className="userorder-primary-btn"
                  onClick={handleContactSupport}
                >
                  Contact Support
                </button>
                <button
                  className="userorder-secondary-btn"
                  onClick={handleBackToOrders}
                >
                  View My Orders
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>

      <ProductReviewModal
        isOpen={showReviewModal}
        onClose={resetReviewModal}
        isMobile={isMobile}
        selectedReviewItem={selectedReviewItem}
        reviewFormData={reviewFormData}
        onRatingChange={handleReviewRatingChange}
        onTextChange={handleReviewTextChange}
        onSubmit={handleSubmitReview}
        isSubmitting={isSubmittingReview}
      />
    </div>
  );
}

export default Userorder;
